import { useCallback, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useDayNavigation } from '~/hooks/useDayNavigation'
import { BreakdownPanel } from '~/pages/BalanceGenerationDetail/components/BreakdownPanel'
import { DetailChart } from '~/pages/BalanceGenerationDetail/components/DetailChart'
import { FilterCard } from '~/pages/BalanceGenerationDetail/components/FilterCard'
import { KomaTable } from '~/pages/BalanceGenerationDetail/components/KomaTable'
import { useGenerationDetail } from '~/pages/BalanceGenerationDetail/hooks/useGenerationDetail'
import { routePaths } from '~/routes/routePaths'

export const BalanceGenerationDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  // ?bg= が無ければ空文字。無効なデフォルト (旧 'TH01' 等の OCCTO 形式外) を撃つと
  // backend が CodeInvalidArgument を返し永久ローディング + 5 秒失敗ループになるため、
  // 未指定はガイダンス表示にフォールバックする (query は hook 側で enabled=false)。
  const bgCode = searchParams.get('bg') ?? ''

  const { displayDate, setDisplayDate, goToToday } = useDayNavigation()
  /**
   * 本体 3 ペインの器。 **正常時とエラー時 (空状態) で同じものを使う** — 別々に
   * 書くと片方だけ幅や gap が動いて、 エラーから復帰した瞬間にレイアウトが飛ぶ。
   */
  const bodyGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '280px 1fr 260px',
    gap: '12px',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    paddingTop: '8px',
  }

  /** 左 (テーブル) / 中 (チャート) ペインの器。 */
  const paneStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  }

  // 基本表示トグル (計画線/実績線)。design hatsu-detail.html は両方 default ON。
  // 発電形式トグルは積み上げデータ整備 (#2807) とセットで追加する。
  const [seriesVisibility, setSeriesVisibility] = useState<
    Record<'genPlan' | 'genActual', boolean>
  >({ genPlan: true, genActual: true })
  const toggleSeries = useCallback((key: 'genPlan' | 'genActual'): void => {
    setSeriesVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const clearSeries = useCallback((): void => {
    setSeriesVisibility({ genPlan: true, genActual: true })
  }, [])

  // 前日 / 翌日 / 本日リセットは共通 `useDayNavigation` (FilterCard > DateNav が
  // 暦日で計算して `onDateChange` を呼ぶ) に委譲するため、ページ側の日送り
  // callback は持たない。

  const {
    data: detail,
    isRegionLoading,
    isError,
  } = useGenerationDetail(displayDate, bgCode)

  const breadcrumbItems = [
    {
      label: t('common:nav.balance'),
      to: routePaths.balanceGeneration.path,
    },
    {
      label: t(routePaths.balanceGeneration.name),
      to: routePaths.balanceGeneration.path,
    },
    {
      label: detail?.bgConfig.name ?? bgCode,
      bold: true,
    },
  ]

  return (
    <WorkflowPageLayout breadcrumbItems={breadcrumbItems}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {detail && (
          <FilterCard
            bgConfig={detail.bgConfig}
            displayDate={displayDate}
            gateCloseFrontier={detail.gateCloseFrontier}
            gcKoma={detail.gcKoma}
            onClearSeries={clearSeries}
            onDateChange={setDisplayDate}
            onToday={goToToday}
            onToggleSeries={toggleSeries}
            seriesVisibility={seriesVisibility}
          />
        )}

        {/* 取得失敗は**画面上部のエラーバナー**で知らせ、 テーブル・チャートは
            空にする (`doji-hatsu.md` AC-BDH-026)。 中央寄せの Text で本文ごと
            置き換える形だったのを、 他 3 画面 (#7409 / #7415 / #7566) と同じ
            バナーへ揃えた。

            バナーの条件は `isError` **単体**。 AC-BDH-026 は「IF 指定BGのデータが
            取得できない場合」 で初回取得に限定しておらず、 5 秒ポーリングの再取得が
            失敗した時も知らせる必要があるため、 データの有無で出し分けない。

            (初版は `doji-hatsu-plant.md` AC-BDP-015 を根拠に引いていたが、
            **発電所別画面は #7531 (2026-09-08) で spec ごと廃止済み**で、
            そのファイルも AC も存在しない。 2026-09-09 レビュー指摘。)

            「BG 未指定」 はエラーではないのでバナーを出さない (下の中央メッセージ)。 */}
        {bgCode !== '' && isError && (
          <Box marginBottom={3}>
            <CompactAlert
              role="alert"
              status="error"
              title={t('page:balanceGenerationDetail.loadError')}
            />
          </Box>
        )}

        {bgCode === '' ? (
          <Flex
            alignItems="center"
            justifyContent="center"
            flex={1}
            padding={8}
          >
            <Text color="Text.midEmphasis" textAlign="center">
              {t('page:balanceGenerationDetail.noBgSelected')}
            </Text>
          </Flex>
        ) : isError /* 取得に失敗している間は**本体 (テーブル・チャート・内訳) を描かない**。

             [spec 差異] AC-BDH-026 の文言は「テーブル・チャートを**空状態にする**」 で、
             初版は列見出しだけの空テーブル・空チャート・内訳パネルを出していた。
             2026-09-09 デザイナー判断 (#7566 で同型の指摘)「alert の下にある blank の
             部品は非表示で良さそう」 に揃えて**非表示**にする。

             空の器を出すと実害もあった: 内訳パネルが「合計 0 kWh 100%」 と、
             **取得できていない値を確定値の顔で**描いていた (`GcCountdownCard` の
             「次の GC コマ1」 と同じ問題)。 器ごと出さなければ起きない。

             AC の意図 (古い値を残さない) は満たしたまま。 TanStack Query は refetch
             失敗で `data` を保持したまま `isError` を立てるので、 初回か背景 refetch かを
             問わず `isError` 単体で落とす (2026-09-09 レビュー指摘)。 spec 側の追随は #7064。 */ ? null : (
          <RegionLoading
            flexDirection="column"
            flex="1"
            display="flex"
            minHeight={!detail ? '200px' : 0}
            // 表示日・BG を変えた後は前の結果を残したまま覆う (UI 辞書 components/region-loading.md)
            isLoading={isRegionLoading}
          >
            {!detail ? null : (
              <div style={bodyGridStyle}>
                <div style={paneStyle}>
                  <KomaTable
                    currentKoma={detail.currentKoma}
                    data={detail.komas}
                  />
                </div>

                <div style={paneStyle}>
                  {/* チャート上の summary は撤去済み (#2942: MAX/MIN + 平均利用率 と 発電種別は
                  8080 にも spec にも無く、 常に無意味な値しか出せなかった)。
                  「次の GC」 (spec AC-BDH-019b / 019c) は、 UI 辞書 toolbar-collapsible.md
                  「カウントダウン」 に従い開閉式フィルターのツールバー右側に置く (FilterCard)。 */}
                  <DetailChart
                    data={detail.komas}
                    displayDate={displayDate}
                    gcKoma={detail.gcKoma}
                    seriesVisibility={seriesVisibility}
                  />
                </div>

                <BreakdownPanel breakdown={detail.breakdown} />
              </div>
            )}
          </RegionLoading>
        )}
      </div>
    </WorkflowPageLayout>
  )
}
